export interface CarouselConfig {
    title: string;
    displayName: string;
    platform: string;
    type: string;
    contentShape: 'rect' | 'sq';
    ctaPlaceholder: string;
    sortOrder: 'DESC' | 'ASC';
}

/** Max characters for a carousel title (dashboard + public). */
export const MAX_CAROUSEL_TITLE_CHARS = 60;

export const CAROUSEL_ORDER_PILL_OPTIONS = [
    { id: "DESC" as const, label: "Newest to Oldest" },
    { id: "ASC" as const, label: "Oldest to Newest" },
    { id: "CUSTOM" as const, label: "Custom" },
];

export const CAROUSEL_SHAPE_PILL_OPTIONS = [
    { id: "rect" as const, label: "Rectangle" },
    { id: "sq" as const, label: "Square" },
];

export const CAROUSEL_SOUND_PILL_OPTIONS = [
    { id: "on" as const, label: "On" },
    { id: "off" as const, label: "Off" },
];

export const CAROUSEL_SHOPPABLE_PILL_OPTIONS = [
    { id: "all" as const, label: "All" },
    { id: "shoppable" as const, label: "Shoppable" },
    { id: "non-shoppable" as const, label: "Non-Shoppable" },
];

export const CAROUSEL_SHOW_PILL_OPTIONS = [
    { id: "both" as const, label: "Both" },
    { id: "content" as const, label: "Content" },
    { id: "products" as const, label: "Products" },
];

export const DATE_RANGE_PRESET_OPTIONS = [
    { label: "All", key: 2 },
    { label: "Last 7 days", key: 7 },
    { label: "Last 30 days", key: 30 },
    { label: "Last 90 days", key: 90 },
    { label: "Custom Date range", key: 1 },
] as const;

export const MAX_CAROUSEL_DESCRIPTION_CHARS = 120;

export const CAROUSEL_DESCRIPTION_PLACEHOLDER = "Add a description";

export function trimCarouselTitle(value: string) {
    return String(value || "").slice(0, MAX_CAROUSEL_TITLE_CHARS);
}

/** Visible carousel title: user text if they edited, otherwise the default prefill. */
export function resolveEditableTitle(
    name: string,
    previewTitle: string,
    hasUserEditedName: boolean
) {
    if (hasUserEditedName) return String(name ?? "");
    return String(previewTitle || name || "");
}

export function getMaxCarouselItemsError(
    max: number,
    kind: "products" | "posts"
): string {
    return `Maximum ${max} ${kind} allowed.`;
}

export function getCarouselTitleLengthError(title: string): string | null {
    const trimmed = String(title || "").trim();
    if (trimmed.length > MAX_CAROUSEL_TITLE_CHARS) {
        return `Title must be ${MAX_CAROUSEL_TITLE_CHARS} characters or less`;
    }
    return null;
}

export function isTitleFieldError(message?: string | null): boolean {
    const msg = String(message || "").trim().toLowerCase();
    if (!msg) return false;
    if (msg.includes("already taken") || msg.includes("already exists")) {
        return false;
    }
    return (
        msg === "title is required" ||
        msg === "title cannot be empty" ||
        msg === "invalid title" ||
        msg.startsWith("title must be") ||
        msg.startsWith("title cannot exceed") ||
        msg.includes("title is reserved")
    );
}

export function nonTitleFieldError(message?: string | null): string {
    return isTitleFieldError(message) ? "" : String(message || "").trim();
}

export function parseMinimumClicksInput(value: string): {
    nextValue: string;
    error: string;
    isValid: boolean;
} {
    if (value === "") {
        return { nextValue: "", error: "", isValid: false };
    }
    const numericValue = parseInt(value, 10);
    if (Number.isNaN(numericValue) || numericValue < 1) {
        return {
            nextValue: "",
            error: "Number should not be less than 1",
            isValid: false,
        };
    }
    return { nextValue: String(numericValue), error: "", isValid: true };
}

export function formatTrendingContentValue(
    clickNumber: string,
    startDate: string,
    endDate: string,
    activeTab: number | string
) {
    return `${clickNumber},${startDate},${endDate},${activeTab}`;
}

export const ACP_PREVIEW_EMPTY_MESSAGE = "No content added yet";

/** Matches `--content-post-gap` used by carousel posts. */
export const CAROUSEL_PREVIEW_GAP = 12;

/**
 * How many tiles to render so a row is covered, including one clipped tile
 * for leftover width. Historical add-modal loaders used 3 as the floor.
 */
export function countItemsToFillRow(
    containerWidth: number,
    itemWidth: number,
    gap: number,
    minCount = 3
) {
    const width = Number(containerWidth) || 0;
    const tile = Number(itemWidth) || 0;
    const spacing = Number(gap) || 0;
    if (width <= 0 || tile <= 0) return minCount;
    return Math.max(minCount, Math.ceil((width + spacing) / (tile + spacing)));
}

export function trimCarouselDescription(value: string) {
    return String(value || "")
        .replace(/[\r\n]+/g, " ")
        .slice(0, MAX_CAROUSEL_DESCRIPTION_CHARS);
}

export function getCarouselDescriptionLengthError(description: string): string | null {
    const trimmed = String(description || "").trim();
    if (trimmed.length > MAX_CAROUSEL_DESCRIPTION_CHARS) {
        return `Description must be ${MAX_CAROUSEL_DESCRIPTION_CHARS} characters or less`;
    }
    return null;
}

/** Custom Product carousels are stored as type `"links"`. */
export function carouselSupportsDescription(type?: string | null) {
    return String(type || "").toLowerCase() === "links";
}

/**
 * Website `template_components` has no `description` column.
 * Custom Products (`type: "links"`) keep the blurb in details JSON only.
 */
const COLLECTION_DESCRIPTION_DETAILS_KEY = "collectionDescription";

export function parseCollectionDescriptionFromDetails(
    details?: string | null,
): string {
    const raw = String(details || "").trim();
    if (!raw.startsWith("{")) return "";
    try {
        const parsed = JSON.parse(raw) as Record<string, unknown>;
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
            return "";
        }
        return trimCarouselDescription(
            String(parsed[COLLECTION_DESCRIPTION_DETAILS_KEY] || ""),
        ).trim();
    } catch {
        return "";
    }
}

export function mergeCollectionDescriptionIntoDetails(
    details: string | undefined | null,
    description: string,
): string {
    const desc = trimCarouselDescription(description).trim();
    const raw = String(details || "").trim();
    // Never rewrite non-JSON details (trending CSV, layout tokens, etc.).
    if (raw && !raw.startsWith("{")) return raw;
    let obj: Record<string, unknown> = {};
    if (raw.startsWith("{")) {
        try {
            const parsed = JSON.parse(raw) as Record<string, unknown>;
            if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
                obj = { ...parsed };
            } else {
                return raw;
            }
        } catch {
            return raw;
        }
    }
    if (desc) obj[COLLECTION_DESCRIPTION_DETAILS_KEY] = desc;
    else delete obj[COLLECTION_DESCRIPTION_DETAILS_KEY];
    if (Object.keys(obj).length === 0) return "";
    return JSON.stringify(obj);
}

export type CarouselDescriptionContext = {
    from?: string | null;
    isEmbedComponent?: boolean;
    /** Dedicated individual/detail page (not dashboard, landing carousel, or embed). */
    isIndividualPage?: boolean;
};

export function isCarouselDescriptionEmbedSurface(
    from?: string | null,
    isEmbedComponent?: boolean
) {
    if (isEmbedComponent) return true;
    return String(from || "").toLowerCase() === "embed-search";
}

/**
 * Heading text for Custom Product descriptions.
 * Shown on individual pages (including collection-created pages) and not on
 * dashboard, landing carousels, embed pages, or embed individual views.
 * Edit-modal preview uses its own heading and does not call this.
 */
export function visibleCarouselDescription(
    component?: {
        type?: string | null;
        description?: string | null;
    } | null,
    context?: CarouselDescriptionContext | null
) {
    if (!carouselSupportsDescription(component?.type)) return "";
    const text = String(component?.description || "").trim();
    if (!text) return "";
    if (isCarouselDescriptionEmbedSurface(context?.from, context?.isEmbedComponent)) {
        return "";
    }
    if (!context?.isIndividualPage) return "";
    return text;
}

// List of reserved content carousel titles
export const RESERVED_CONTENT_TITLES = [
    'Latest Instagram Stories',
    'Latest TikToks',
    'Latest Instagram Posts & Reels',
    'Latest Instagram Reels',
    'Latest Instagram Posts',
    'Latest YouTube Videos',
    'Latest Podcast Episodes',
    'Latest LinkedIn Posts',
    'Latest X Posts',
    'Featured Content',
    "This Week's Content",
    'Trending Products',
    'Trending Content'
];

// List of content carousel display names
export const CONTENT_DISPLAY_NAMES = [
    'instagram story',
    'tiktok',
    'reels',
    'youtube',
    'podcast',
    'featured',
    'This Week Content',
    'Trending Products',
    'Trending Content'
];

export const getCarouselConfig = (carouselType: string): CarouselConfig => {
    switch (carouselType.toLowerCase()) {
        case 'instagram story':
            return {
                title: 'Latest Instagram Stories',
                displayName: 'instagram story',
                platform: 'instagram',
                type: 'STORY',
                contentShape: 'rect',
                ctaPlaceholder: 'Check it out!',
                sortOrder: 'DESC'
            };

        case 'tiktok':
            return {
                title: 'Latest TikToks',
                displayName: 'tiktok',
                platform: 'tiktok',
                type: 'TIKTOK',
                contentShape: 'rect',
                ctaPlaceholder: 'Check it out!',
                sortOrder: 'DESC'
            };

        case 'reels':
            return {
                title: 'Latest Instagram Posts & Reels',
                displayName: 'reels',
                platform: 'instagram',
                type: 'REELS',
                contentShape: 'rect',
                ctaPlaceholder: 'Check it out!',
                sortOrder: 'DESC'
            };

        case 'youtube':
            return {
                title: 'Latest YouTube Videos',
                displayName: 'youtube',
                platform: 'youtube',
                type: 'VIDEO',
                contentShape: 'rect',
                ctaPlaceholder: 'Check it out!',
                sortOrder: 'DESC'
            };

        case 'podcast':
            return {
                title: 'Latest Podcast Episodes',
                displayName: 'podcast',
                platform: 'podcast',
                type: 'PODCAST',
                contentShape: 'rect',
                ctaPlaceholder: 'Check it out!',
                sortOrder: 'DESC'
            };

        case 'featured':
            return {
                title: 'Featured Content',
                displayName: 'featured',
                platform: 'featured',
                type: 'FEATURE',
                contentShape: 'rect',
                ctaPlaceholder: 'Check it out!',
                sortOrder: 'DESC'
            };

        case 'this week content':
            return {
                title: "This Week's Content",
                displayName: 'This Week Content',
                platform: 'all',
                type: 'THIS_WEEK',
                contentShape: 'rect',
                ctaPlaceholder: 'Check it out!',
                sortOrder: 'DESC'
            };
        case 'trending content':
            return {
                title: "Trending Content",
                displayName: 'Trending Content',
                platform: 'all',
                type: 'trending content',
                contentShape: 'rect',
                ctaPlaceholder: 'Check it out!',
                sortOrder: 'DESC'
            }
        case 'trending products':
            return {
                title: "Trending Products",
                displayName: 'Trending Products',
                platform: 'all',
                type: 'trending products',
                contentShape: 'rect',
                ctaPlaceholder: 'Check it out!',
                sortOrder: 'DESC'
            }

        default:
            throw new Error(`Unknown carousel type: ${carouselType}`);
    }
};

// Helper function to check if a carousel type is valid
export const isValidCarouselType = (carouselType: string): boolean => {
    const validTypes = [
        'instagram story',
        'tiktok',
        'reels',
        'youtube',
        'podcast',
        'featured',
        'this week content',
        'trending products',
        'trending content'
    ];
    return validTypes.includes(carouselType.toLowerCase());
};

// Helper function to get platform icon type
export const getCarouselPlatformType = (carouselType: string): string => {
    const config = getCarouselConfig(carouselType);
    return config.platform;
};

// Helper function to validate shop links carousel title
export const validateShopLinksTitle = (title: string, carouselType?: string): { isValid: boolean; error?: string } => {
    const lengthError = getCarouselTitleLengthError(title);
    if (lengthError) {
        return { isValid: false, error: lengthError };
    }

    // If it's a trending products carousel, allow the reserved title
    if (carouselType?.toLowerCase() === 'trending products' && title === 'Trending Products') {
        return { isValid: true };
    }

    if (RESERVED_CONTENT_TITLES.includes(title)) {
        return {
            isValid: false,
            error: 'This title is reserved for content carousels. Please choose a different title.'
        };
    }
    return { isValid: true };
};

// Helper function to get valid reserved titles for a display_name
const getValidReservedTitlesForDisplayName = (displayName: string): string[] => {
    const displayNameLower = displayName.toLowerCase();
    
    // For Reels, all three reserved titles are valid
    if (displayNameLower === 'reels') {
        return [
            'Latest Instagram Posts & Reels',
            'Latest Instagram Reels',
            'Latest Instagram Posts'
        ];
    }
    
    // For other content types, get the title from config
    try {
        const config = getCarouselConfig(displayName);
        return [config.title];
    } catch {
        return [];
    }
};

// Helper function to validate title during edit operations
export const validateTitleForEdit = (
    newTitle: string,
    component: { display_name: string; name: string },
    _validateLength?: boolean,
): { isValid: boolean; error?: string } => {
    newTitle = (newTitle || '').trim();
    if (!newTitle) {
        return { isValid: false, error: "Title is required" };
    }
    const lengthError = getCarouselTitleLengthError(newTitle);
    if (lengthError) {
        return { isValid: false, error: lengthError };
    }
    // Normalize display_name for case-insensitive comparison
    const normalizedDisplayName = component.display_name?.toLowerCase().trim();
    const isContentCarousel = CONTENT_DISPLAY_NAMES.some(
        (name) => name.toLowerCase() === normalizedDisplayName
    );
    
    // If it's a content carousel (check by display_name), allow editing its own title
    if (isContentCarousel) {
        // Get valid reserved titles for this display_name (use original display_name for config lookup)
        const validReservedTitles = getValidReservedTitlesForDisplayName(component.display_name);
        
        // If the new title is a reserved title, check if it's valid for this display_name
        if (RESERVED_CONTENT_TITLES.includes(newTitle)) {
            // Allow if it's the original name or one of the valid reserved titles for this display_name
            if (newTitle === component.name || validReservedTitles.includes(newTitle)) {
                return { isValid: true };
            }
            
            // Otherwise, it's trying to change to a different reserved title
            return {
                isValid: false,
                error: 'Cannot change to a different reserved content carousel title.'
            };
        }

        // Allow keeping the same title or changing to a non-reserved title
        return { isValid: true };
    }

    // For non-content carousels, don't allow using any reserved titles
    if (RESERVED_CONTENT_TITLES.includes(newTitle)) {
        return {
            isValid: false,
            error: 'This title is reserved for content carousels. Please choose a different title.'
        };
    }

    return { isValid: true };
};

/** @deprecated Duplicate titles are allowed. Always returns valid. */
export const validateTitleUnique = (
    _newTitle: string,
    _currentComponentId: string,
    _existingComponents: Array<{
        id?: string;
        component_id?: string;
        name?: string;
        status?: string;
        status_expiry?: string;
    }>
): { isValid: boolean; error?: string } => {
    return { isValid: true };
};

const REELS_TYPE = "REELS";

const toLowerValue = (value?: string | null) =>
    (value || "both").toString().toLowerCase();

const normalizeStandardFilter = (value?: string | null) => {
    const lower = toLowerValue(value);

    if (lower === "content" || lower === "products" || lower === "both") {
        return lower;
    }
    if (lower === "reels") {
        return "content";
    }
    if (lower === "post") {
        return "products";
    }
    return "both";
};

const normalizeReelsFilter = (value?: string | null) => {
    const lower = toLowerValue(value);

    // Instagram Show "All" (posts + reels + stories) — before "both" default.
    if (lower === "all") {
        return "all";
    }
    if (lower === "content" || lower === "reels") {
        return "reels";
    }
    if (lower === "products" || lower === "post") {
        return "post";
    }
    return "both";
};

export const isReelsContentType = (type?: string) =>
    (type || "").toUpperCase() === REELS_TYPE;

export const normalizeFilterForType = (value?: string | null, type?: string) => {
    if (isReelsContentType(type)) {
        return normalizeReelsFilter(value);
    }
    return normalizeStandardFilter(value);
};

export const mapReelsFilterToFeedTypes = (value?: string | null) => {
    const normalized = normalizeReelsFilter(value);

    // Instagram Show "All" — posts + reels + stories.
    if (normalized === "all") {
        return ["FEED", "REELS", "STORY"];
    }
    if (normalized === "reels") {
        return ["REELS"];
    }
    if (normalized === "post") {
        return ["FEED"];
    }
    return ["REELS", "FEED"];
};

const STORY_ROW_TYPES = new Set(["STORY", "HIGHLIGHTS"]);
const POST_OR_REEL_TYPES = new Set(["FEED", "POST", "REELS"]);

/**
 * Instagram Show only. Drops a row when `type` explicitly disagrees with the
 * request. Rows with no type are kept so other platforms are unchanged.
 * Posts & Reels omit stories. Stories omit posts and reels.
 */
export function filterRowsByRequestedFeedTypes(
    rows: unknown[],
    feedTypes: string[],
): unknown[] {
    const requested = feedTypes
        .map((t) => String(t || "").trim().toUpperCase())
        .filter(Boolean);
    if (!requested.length || !rows?.length) return rows;
    const wantsStory = requested.some((t) => STORY_ROW_TYPES.has(t));
    const storyOnly = wantsStory && requested.every((t) => STORY_ROW_TYPES.has(t));
    return rows.filter((raw) => {
        if (!raw || typeof raw !== "object") return false;
        const type = String((raw as { type?: unknown }).type ?? "")
            .trim()
            .toUpperCase();
        if (!type) return true;
        if (storyOnly) return !POST_OR_REEL_TYPES.has(type);
        if (!wantsStory && STORY_ROW_TYPES.has(type)) return false;
        return true;
    });
}

/** Use saved carousel sound when set; otherwise fall back to app default (true). */
export const normalizeSoundValue = (value: unknown, fallback: boolean): boolean => {
    if (typeof value === "boolean") return value;
    const s = String(value ?? "").trim().toLowerCase();
    if (!s) return fallback;
    if (s === "true" || s === "on" || s === "1") return true;
    if (s === "false" || s === "off" || s === "0") return false;
    return fallback;
};

/** Use saved shape when valid; otherwise fall back to default ("rect"). */
export const normalizeShapeValue = (value: unknown, fallback: string): string => {
    const shape = String(value || "").toLowerCase().trim();
    if (shape === "rect" || shape === "sq") return shape;
    return fallback;
};

/** Use saved order when set; otherwise fall back to default ("DESC"). */
export const normalizeOrderValue = (value: unknown, fallback: string): string => {
    if (value !== undefined && value !== null && String(value).trim() !== "") {
        return String(value).trim();
    }
    return fallback;
};

/** Use saved show filter when set; otherwise fall back to provided default. */
export const normalizeActiveFilterValue = (
    value: unknown,
    fallback: string
): string => {
    if (value !== undefined && value !== null && String(value).trim() !== "") {
        return String(value).trim();
    }
    return fallback;
};

export type ShoppableStatusFilter = "all" | "shoppable" | "non-shoppable";

export function normalizeShoppableStatusFilter(
    value: unknown
): ShoppableStatusFilter {
    const normalized = String(value || "all").trim().toLowerCase();
    if (normalized === "shoppable" || normalized === "non-shoppable") {
        return normalized;
    }
    return "all";
}

const hasValidShopLink = (value: any): boolean => {
    if (!value) return false;
    if (typeof value === "string") {
        return value.trim().length > 0;
    }
    if (Array.isArray(value)) {
        return value.some((entry) => hasValidShopLink(entry));
    }
    if (typeof value === "object") {
        return (
            hasValidShopLink(value.url) ||
            hasValidShopLink(value.href) ||
            hasValidShopLink(value.link) ||
            Object.values(value).some((entry) => hasValidShopLink(entry))
        );
    }
    return false;
};

/** A post is shoppable when it has a shop link (same signal as the shopping-bag icon). */
export function isPostShoppable(post: any): boolean {
    return hasValidShopLink(post?.shop_link) || hasValidShopLink(post?.shopLink);
}

/**
 * True for Amazon / LTK / ShopMy / MISC / MISC-product (any casing).
 * Used by PostMetadata + shop-bag gating so MISC-product is treated as product.
 */
export function isProductPlatformName(platform?: string | null): boolean {
    const p = String(platform || "").trim().toLowerCase();
    if (!p) return false;
    return (
        p === "ltk" ||
        p === "shopmy" ||
        p === "amazon" ||
        p === "misc" ||
        p === "misc-product" ||
        p.startsWith("misc")
    );
}

/**
 * Socials for Custom Products browse/search "All".
 * PRODUCT_PLATFORMS is LTK/ShopMy/Amazon/MISC — also include MISC-product so
 * manually added products are findable (matches MISC pill expansion).
 */
export function getCustomProductBrowseSocials(
    productPlatforms: string[],
): string[] {
    const out: string[] = [];
    const seen = new Set<string>();
    for (const raw of [...productPlatforms, "MISC-product"]) {
        const p = String(raw || "").trim();
        if (!p || seen.has(p)) continue;
        seen.add(p);
        out.push(p);
    }
    return out;
}

/** Bag shop button on content cards: shop_link exists and platform is not a product platform. */
export function shouldShowContentShopBag(platform?: string, shopLink?: any) {
    if (!shopLink) return false;
    return !isProductPlatformName(platform);
}

export function applyShoppableFilter(items: any[], status?: string): any[] {
    if (!Array.isArray(items) || !status || status === "all") {
        return items;
    }

    return items.filter((item) => {
        const shoppable = isPostShoppable(item);
        return status === "shoppable" ? shoppable : !shoppable;
    });
}